// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

export interface SubtransactionSummary {
  id: string;
  transaction_id: string;
  amount: number;
  memo?: string | null;
  payee_id?: string | null;
  payee_name?: string | null;
  category_id?: string | null;
  category_name?: string | null;
  transfer_account_id?: string | null;
  transfer_transaction_id?: string | null;
  deleted: boolean;
}

export interface YnabDebtTransactionType {
  original_balance: number | null;
  interest_rates: Record<string, number>;
  minimum_payments: Record<string, number>;
  escrow_amounts: Record<string, number>;
}

// Subset of YNAB types needed for collections

export type AccountType = 'checking' | 'savings' | 'cash' | 'creditCard' | 'lineOfCredit' | 'otherAsset' | 'otherLiability' | 'mortgage' | 'autoLoan' | 'studentLoan' | 'personalLoan' | 'medicalDebt' | 'otherDebt';

export type TransactionClearedStatus = 'cleared' | 'uncleared' | 'reconciled';

export type TransactionFlagColor = 'red' | 'orange' | 'yellow' | 'green' | 'blue' | 'purple' | '' | null;

export type TransactionFlagName = string | null;

export type ScheduledTransactionFrequency = 'never' | 'daily' | 'weekly' | 'everyOtherWeek' | 'twiceAMonth' | 'every4Weeks' | 'monthly' | 'everyOtherMonth' | 'every3Months' | 'every4Months' | 'twiceAYear' | 'yearly' | 'everyOtherYear';

export type LoanAccountPeriodicValue = {
    [key: string]: number;
} | null;

export type Account = {
    id: string;
    name: string;
    type: AccountType;
    on_budget: boolean;
    closed: boolean;
    note?: string | null;
    balance: number;
    cleared_balance: number;
    uncleared_balance: number;
    transfer_payee_id: string | null;
    direct_import_linked?: boolean;
    direct_import_in_error?: boolean;
    last_reconciled_at?: string | null;
    debt_original_balance?: number | null;
    debt_interest_rates?: LoanAccountPeriodicValue;
    debt_minimum_payments?: LoanAccountPeriodicValue;
    debt_escrow_amounts?: LoanAccountPeriodicValue;
    deleted: boolean;
};

export type Category = {
    id: string;
    category_group_id: string;
    category_group_name?: string;
    name: string;
    hidden: boolean;
    original_category_group_id?: string | null;
    note?: string | null;
    budgeted: number;
    activity: number;
    balance: number;
    goal_type?: 'TB' | 'TBD' | 'MF' | 'NEED' | 'DEBT' | null;
    goal_needs_whole_amount?: boolean | null;
    goal_day?: number | null;
    goal_cadence?: number | null;
    goal_cadence_frequency?: number | null;
    goal_creation_month?: string | null;
    goal_target?: number | null;
    goal_target_month?: string | null;
    goal_percentage_complete?: number | null;
    goal_months_to_budget?: number | null;
    goal_under_funded?: number | null;
    goal_overall_funded?: number | null;
    goal_overall_left?: number | null;
    goal_snoozed_at?: string | null;
    deleted: boolean;
};

export type TransactionSummary = {
    id: string;
    date: string;
    amount: number;
    memo?: string | null;
    cleared: TransactionClearedStatus;
    approved: boolean;
    flag_color?: TransactionFlagColor;
    flag_name?: TransactionFlagName;
    account_id: string;
    payee_id?: string | null;
    category_id?: string | null;
    transfer_account_id?: string | null;
    transfer_transaction_id?: string | null;
    matched_transaction_id?: string | null;
    import_id?: string | null;
    import_payee_name?: string | null;
    import_payee_name_original?: string | null;
    debt_transaction_type?: 'payment' | 'refund' | 'fee' | 'interest' | 'escrow' | 'balanceAdjustment' | 'credit' | 'charge' | null;
    deleted: boolean;
};

export type SubTransaction = {
    id: string;
    transaction_id: string;
    amount: number;
    memo?: string | null;
    payee_id?: string | null;
    payee_name?: string | null;
    category_id?: string | null;
    category_name?: string | null;
    transfer_account_id?: string | null;
    transfer_transaction_id?: string | null;
    deleted: boolean;
};

export type TransactionDetail = TransactionSummary & {
    account_name: string;
    payee_name?: string | null;
    category_name?: string | null;
    subtransactions: Array<SubTransaction>;
};

export type ScheduledTransactionSummary = {
    id: string;
    date_first: string;
    date_next: string;
    frequency: ScheduledTransactionFrequency;
    amount: number;
    memo?: string | null;
    flag_color?: TransactionFlagColor;
    flag_name?: TransactionFlagName;
    account_id: string;
    payee_id?: string | null;
    category_id?: string | null;
    transfer_account_id?: string | null;
    deleted: boolean;
};

export type ScheduledSubTransaction = {
    id: string;
    scheduled_transaction_id: string;
    amount: number;
    memo?: string | null;
    payee_id?: string | null;
    payee_name?: string | null;
    category_id?: string | null;
    category_name?: string | null;
    transfer_account_id?: string | null;
    deleted: boolean;
};

export type ScheduledTransactionDetail = ScheduledTransactionSummary & {
    account_name: string;
    payee_name?: string | null;
    category_name?: string | null;
    subtransactions: Array<ScheduledSubTransaction>;
};

/**
 * Shared base types and interfaces for all RxDB collections
 * Provides consistent patterns across the db-collections package
 */

/**
 * Base fields that all collection documents should have
 */
export interface BaseDocument {
  /** Unique identifier for the document */
  id: string;
  /** ID of the organization this document belongs to */
  orgId: string;
  /** ID of the user who created this document */
  userId: string;
  /** UTC timestamp when the document was created */
  createdAt: string;
  /** UTC timestamp when the document was last updated */
  updatedAt: string;
}

/**
 * Base schema properties that all collections should include
 * Use with spread operator in collection schemas
 */
export const baseSchemaProperties = {
  id: { type: 'string', maxLength: 100 },
  orgId: { type: 'string', maxLength: 100 },
  userId: { type: 'string', maxLength: 100 },
  createdAt: { type: 'string', format: 'date-time', maxLength: 100 },
  updatedAt: { type: 'string', format: 'date-time', maxLength: 100 },
} as const;

/**
 * Base required fields that all collections should enforce
 */
export const baseRequiredFields = [
  'id',
  'orgId',
  'userId',
  'createdAt',
  'updatedAt',
] as const;

/**
 * Common metadata patterns for tracking document statistics
 */
export interface BaseMetadata {
  [key: string]: unknown;
}

/**
 * Soft delete pattern - use for collections that need soft delete
 */
export interface SoftDeletable {
  /** Whether this document is soft deleted */
  isDeleted: boolean;
  /** UTC timestamp when the document was soft deleted */
  deletedAt?: string;
}

/**
 * Edit tracking pattern - use for collections that need edit history
 */
export interface EditTracking {
  /** Whether this document has been edited after creation */
  isEdited: boolean;
  /** UTC timestamp when the document was last edited */
  editedAt?: string;
}

/**
 * Threading pattern - use for collections that support nested comments/replies
 */
export interface Threadable {
  /** ID of the parent document for nested replies */
  parentId?: string;
  /** Depth level in the thread tree (0 for root) */
  threadDepth: number;
}

/**
 * Polymorphic relationship pattern - use for collections that reference multiple entity types
 */
export interface PolymorphicEntity {
  /** Type of the entity this references */
  entityType: string;
  /** ID of the entity this references */
  entityId: string;
}

/**
 * Common schema properties for soft delete functionality
 */
export const softDeleteSchemaProperties = {
  isDeleted: { type: 'boolean' },
  deletedAt: { type: 'string', format: 'date-time', maxLength: 100 },
} as const;

/**
 * Common schema properties for edit tracking functionality
 */
export const editTrackingSchemaProperties = {
  isEdited: { type: 'boolean' },
  editedAt: { type: 'string', format: 'date-time', maxLength: 100 },
} as const;

/**
 * Common schema properties for threading functionality
 */
export const threadableSchemaProperties = {
  parentId: { type: 'string', maxLength: 100 },
  threadDepth: { type: 'number', minimum: 0, maximum: 10 },
} as const;

/**
 * Common schema properties for polymorphic relationships
 */
export const polymorphicEntitySchemaProperties = {
  entityType: { type: 'string', maxLength: 100 },
  entityId: { type: 'string', maxLength: 100 },
} as const;

/**
 * Conversation Management Types
 *
 * Defines data structures for managing multi-turn conversations in agent tasks,
 * including support for conversation history compression and context optimization.
 */

/**
 * Conversation turn in agent task execution
 */
export interface ConversationTurn {
  /** Turn number in conversation (1-indexed) */
  turnNumber: number;

  /** Role: user, assistant, or system */
  role: 'user' | 'assistant' | 'system';

  /** Message content */
  content: string;

  /** ISO timestamp */
  timestamp: string;

  /** Optional metadata about this turn */
  metadata?: TurnMetadata;
}

/**
 * Metadata for a conversation turn
 */
export interface TurnMetadata {
  /** Tools used during this turn (summary) */
  toolsUsed?: string[];

  /** Files modified during this turn */
  filesModified?: string[];

  /** Commands executed (summary) */
  commandsExecuted?: string[];

  /** Token usage for this turn */
  tokens?: {
    input: number;
    output: number;
  };

  /** Cost for this turn (USD) */
  costUsd?: number;

  /** Whether this turn blocked waiting for input */
  blockedForInput?: boolean;

  /** Agent ID that processed this turn */
  agentId?: string;

  /** Claude session ID for conversation resumption */
  sessionId?: string;
}

/**
 * Conversation state with compression support
 */
export interface ConversationState {
  /** All conversation turns (recent, uncompressed) */
  turns: ConversationTurn[];

  /** Compressed summary of older turns (if compression applied) */
  compressedHistory?: CompressedHistory;

  /** Total number of turns (including compressed) */
  totalTurns: number;

  /** Configuration used for this conversation */
  config?: ConversationConfig;
}

/**
 * Compressed history segment
 */
export interface CompressedHistory {
  /** Turn numbers that were compressed (e.g., 1-10) */
  turnRange: {
    start: number;
    end: number;
  };

  /** Summary of compressed turns */
  summary: string;

  /** When compression was performed */
  compressedAt: string;

  /** Key facts/decisions from compressed turns */
  keyPoints?: string[];

  /** Estimated tokens saved by compression */
  tokensSaved?: number;
}

/**
 * Configuration for conversation management
 */
export interface ConversationConfig {
  /** Number of recent turns to keep verbatim */
  recentTurnsToKeep: number;

  /** Trigger compression when total turns exceeds this */
  compressionThreshold: number;

  /** Whether to include tool details in summaries */
  includeToolDetails: boolean;

  /** Whether compression is enabled */
  compressionEnabled: boolean;
}

/**
 * Default conversation configuration
 *
 * Optimized for balance between context fidelity and token efficiency:
 * - Keeps last 8 turns verbatim (sufficient for most multi-turn tasks)
 * - Compresses when conversation exceeds 12 turns
 * - Tool summaries only (not full output)
 */
export const DEFAULT_CONVERSATION_CONFIG: ConversationConfig = {
  recentTurnsToKeep: 8,           // Keep last 8 turns verbatim
  compressionThreshold: 12,        // Compress when >12 total turns
  includeToolDetails: false,       // Tool summaries only
  compressionEnabled: true,        // Enable compression
};

/**
 * Process & Workflow Type Definitions
 *
 * Provides comprehensive type safety for the process visualization system
 * that supports flowchart rendering, workflow documentation, and process management.
 */

/**
 * Status of a process in its lifecycle
 */
export type ProcessStatus = 'draft' | 'active' | 'archived' | 'deprecated';

/**
 * Category/domain that a process belongs to
 */
export type ProcessCategory =
  | 'operations'
  | 'engineering'
  | 'hr'
  | 'finance'
  | 'sales'
  | 'marketing'
  | 'support'
  | 'legal'
  | 'compliance'
  | 'other';

/**
 * Type of step in a process flowchart
 */
export type ProcessStepType =
  | 'start'
  | 'end'
  | 'action'
  | 'decision'
  | 'subprocess'
  | 'wait'
  | 'parallel'
  | 'merge';

/**
 * Discussion Feature Type Definitions
 *
 * Provides comprehensive type safety for the discussion system that supports
 * polymorphic relationships with Projects, Milestones, Tasks, and future entities.
 */

/**
 * Supported entity types for discussions
 * Extensible to support future entity types
 */
export type DiscussionEntityType = 'project' | 'milestone' | 'task' | string;

/**
 * File attachment status states
 */
export type AttachmentStatus = 'pending' | 'uploaded' | 'failed';

/**
 * Mention extracted from discussion content
 * Represents an @mention of a user or agent
 */
export interface DiscussionMention {
  /** Type of entity mentioned */
  type: 'agent' | 'user' | 'team';
  /** The @handle used in content (e.g., "engineering-manager-backend") */
  identifier: string;
  /** Resolved TeamMember ID if applicable */
  teamMemberId?: string;
  /** Resolved User ID for human users */
  userId?: string;
  /** Character position in content where mention starts */
  position: number;
}

/**
 * Main discussion model interface
 * Follows existing collection patterns with UTC date strings
 */
export interface DiscussionModel {
  // Base fields (following existing collection patterns)
  id: string;
  orgId: string;
  workspaceId: string;
  userId: string;
  createdAt: string; // UTC ISO string following existing pattern
  updatedAt: string; // UTC ISO string following existing pattern

  // Polymorphic relationship fields
  entityType: DiscussionEntityType;
  entityId: string;

  // Content fields
  content: string; // Markdown text content
  contentHtml?: string; // Optional cached HTML for performance optimization

  // Threading fields
  parentId?: string; // Reference to parent discussion for replies
  threadDepth: number; // 0 for root discussions, increments for nested replies

  // Metadata fields
  isEdited: boolean;
  editedAt?: string; // UTC ISO string when last edited
  isDeleted: boolean;
  deletedAt?: string; // UTC ISO string when soft deleted

  // File attachments
  attachments?: AttachmentModel[];

  // User information (denormalized for performance)
  userName: string;
  userAvatar?: string;

  /** Parsed @mentions from content */
  mentions?: DiscussionMention[];

  /** Emoji reaction tallies, keyed by emoji string (e.g. { "👍": 3 }). */
  reactionCounts?: Record<string, number>;

  // Optional fields for future extensibility
  metadata?: Record<string, unknown>;

  /** Tenant isolation identifier */
  tenantId?: string;

  /** Extensible object for future schema additions */
  extended?: Record<string, unknown>;
}

/**
 * File attachment model interface
 * Stores metadata for files attached to discussions
 */
export interface AttachmentModel {
  id: string;
  name: string; // Original file name
  size: number; // File size in bytes
  type: string; // MIME type (e.g., 'image/png', 'application/pdf')
  url: string; // Secure URL or storage reference
  uploadedAt: string; // UTC ISO string
  status: AttachmentStatus;

  // Optional metadata
  thumbnailUrl?: string; // For image/video previews
  checksum?: string; // For integrity verification
}